import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import type { User } from "@shared/models/auth";
import { normalizeUserBooleans } from "@/lib/userRoles";
import { buildApiUrl, getAuthToken, setAuthToken, clearAuthToken } from "@/lib/apiBase";
import { Capacitor } from "@capacitor/core";

// Build optional Bearer header. On the web `getAuthToken()` returns null, so
// the headers object stays empty and the request is identical to before.
function authHeader(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchUser(): Promise<User | null> {
  const isNative = Capacitor.isNativePlatform();
  const response = await fetch(buildApiUrl("/api/auth/user"), {
    credentials: "include",
    headers: {
      ...authHeader(),
      // Native clients opt-in to receiving a fresh Bearer token in the
      // response so the token is refreshed on every successful session
      // restore — no separate round-trip required.
      ...(isNative ? { "X-Client": "native" } : {}),
    },
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  const raw = await response.json();
  if (!raw || typeof raw !== "object") {
    return null;
  }

  // Extract and store the refreshed token before normalising the user object.
  if (isNative && typeof (raw as any).token === "string" && (raw as any).token.length > 0) {
    setAuthToken((raw as any).token);
  }
  // Strip the token field so it never leaks into the React Query cache.
  const { token: _token, ...userFields } = raw as any;
  return normalizeUserBooleans(userFields as User);
}

async function logout(): Promise<void> {
  try {
    await fetch(buildApiUrl("/api/auth/logout"), {
      method: "POST",
      credentials: "include",
      headers: authHeader(),
    });
  } finally {
    // Always drop the local token on logout — no-op on the web where none is stored.
    clearAuthToken();
    window.location.href = "/";
  }
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.clear();
    },
    onError: () => {
      queryClient.clear();
    },
  });

  // On native Capacitor, if the user is authenticated via session cookie but
  // has no Bearer token stored (e.g. after app reinstall, token expiry, or
  // pre-token registration), fetch a fresh token from the server. This ensures
  // the WebSocket connects via Bearer auth (the reliable path on iOS WKWebView).
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!user) return;
    if (getAuthToken()) return;
    fetch(buildApiUrl("/api/auth/native-token"), {
      method: "POST",
      credentials: "include",
      headers: { "X-Client": "native" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data.token === "string" && data.token.length > 0) {
          setAuthToken(data.token);
        }
      })
      .catch(() => {});
  }, [user]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    isInitialized: !isLoading,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
}
